import React from 'react';
import { cn } from '../../../utils';
import type { Position, ConnectionState } from '../../../types';
import { LoaderIcon, ExpandIcon, BotIcon } from '../../shared/icons';

export interface DefaultFloatingButtonProps {
  /** Click handler */
  onClick: () => void;
  /** Widget position */
  position?: Position;
  /** Avatar image URL */
  avatar?: string;
  /** Agent name for alt text */
  agentName?: string;
  /** Enable pulse animation */
  pulse?: boolean;
  /** Connection state */
  status?: ConnectionState;
  /** Timer text to display when active */
  timerText?: string;
  /** Custom offset */
  offset?: { x?: number; y?: number };
  /** Additional class names */
  className?: string;
}

/**
 * Default style floating button - Default-style pill shape
 */
export function DefaultFloatingButton({
  onClick,
  position = 'bottom-right',
  avatar,
  agentName = 'AI Assistant',
  status = 'idle',
  timerText = '00:00',
  offset,
  className,
}: DefaultFloatingButtonProps) {
  const isLoading = status === 'connecting' || status === 'reconnecting';
  const isActive = status === 'connected';

  const positionStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: offset?.y ?? 24,
    ...(position === 'bottom-left' ? { left: offset?.x ?? 24 } : { right: offset?.x ?? 24 }),
    zIndex: 'var(--qcall-z-widget)',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // Base styles - Pill shape
        'flex items-center gap-3 p-1.5 pl-1.5 pr-4 rounded-full',
        'transition-all duration-300 ease-out',
        'hover:scale-105 active:scale-95 text-slate-800',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        // Background and Shadow
        'bg-[#f5f2efcc] border border-gray-300',
        'shadow-sm hover:shadow-md',
        className,
        isActive && 'pr-2'
      )}
      style={positionStyles}
      aria-label={isActive ? 'Active call - click to expand' : 'Start conversation'}
      aria-expanded={false}
    >
      <div
        className={cn(
          "relative flex items-center justify-center rounded-full shrink-0 overflow-hidden",
          isActive ? "w-6 h-6" : "w-8 h-8"
        )}
      >
        {avatar ? (
          <img src={avatar} alt={agentName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-indigo-500 flex items-center justify-center">
            <BotIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {isLoading ? (
        <LoaderIcon className="w-4 h-4 text-slate-500 animate-spin mr-1" />
      ) : isActive ? (
        <>
          <span className="text-sm font-medium text-slate-600 font-mono tracking-tight">{timerText}</span>
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors ml-1">
            <ExpandIcon className="w-3.5 h-3.5 text-slate-700" />
          </div>
        </>
      ) : (
        <span className="text-[13px] font-medium text-slate-800">Voice chat</span>
      )}
    </button>
  );
}
